import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  defaultDropAnimation,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { ApiContext } from '../../App';
import RecruitmentContainer from "./RecruitmentContainer";
import { RecruitmentItem } from "./SortableRecruitmentItem";
import RecruitmentBulkActions from "./RecruitmentBulkActions";
import EditPosition from "./EditPosition";
import PositionHeader from "../position/PositionHeader";
import ConfirmationDialog from "../utils/ConfirmationDialog";
import { Columns3, Rows3 } from 'lucide-react';
import RecruitmentListView from "./RecruitmentListView";
import AddIndividual from "../individuals/AddIndividual";
import { getAllEmails } from "../../utils/emailHelpers";


// Définition des styles
const wrapperStyle = {
  display: "flex",
  flexDirection: "row",
  gap: "16px",
  paddingBottom: "16px",
  background: "#f5f5f5",
  overflowX: "auto",
  minHeight: "calc(100vh - 200px)",
};

// Configuration de l'animation de drop
const dropAnimation = {
  ...defaultDropAnimation,
  dragSourceOpacity: 0.5,
};

export default function ProcessBoardThree({ positionId }) {
  const API = useContext(ApiContext);
  const navigate = useNavigate();
  const [candidatesByStage, setCandidatesByStage] = useState({});
  const [recruitmentSteps, setRecruitmentSteps] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [positionData, setPositionData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewMode, setViewMode] = useState('kanban');
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false); 



  // État pour les filtres par colonne
  const [columnFilters, setColumnFilters] = useState({});

  // Configuration des sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Chargement initial des données
  useEffect(() => {
    const fetchPositionData = async () => {
      try {
        const response = await API.get(`/api/positions/${positionId}`);
        const positionData = response.data;

        // Récupérer TOUTES les étapes (pour trouver les stageId des candidats)
        const allSteps = positionData.steps.sort((a, b) => a.order - b.order);

        // Filtrer les étapes actives pour l'affichage
        const activeSteps = allSteps.filter(step => step.active);

        setRecruitmentSteps(activeSteps);

        // Groupement des candidats par étape (en cherchant dans TOUTES les étapes)
        const grouped = positionData.candidates.reduce((acc, candidate) => {
          let stageId;

          // Priorité à stageId si disponible
          if (candidate.stageId) {
            // ✅ Chercher dans TOUTES les étapes, pas seulement les actives
            const step = allSteps.find(s => s._id === candidate.stageId);
            stageId = step ? step._id : null;
          }

          // Fallback sur stage (nom) pour compatibilité
          if (!stageId) {
            const step = allSteps.find(s => s.name === candidate.stage);
            stageId = step ? step._id : null;
          }

          // ✅ IMPORTANT : N'ajouter le candidat QUE si son étape est ACTIVE
          if (stageId) {
            const stepIsActive = activeSteps.find(s => s._id === stageId);
            if (stepIsActive) {
              if (!acc[stageId]) acc[stageId] = [];
              acc[stageId].push(candidate);
            }
            // Si l'étape n'est pas active, le candidat reste dans son étape
            // mais n'est pas affiché dans le Kanban
          }

          return acc;
        }, {});

        // S'assurer que toutes les étapes actives existent, même vides
        const completeGrouped = activeSteps.reduce((acc, step) => {
          acc[step._id] = grouped[step._id] || [];
          return acc;
        }, {});

        setCandidatesByStage(completeGrouped);
        setPositionData(positionData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching position data:", err);
        setError("Impossible de charger les données de la position.");
        setLoading(false);
      }
    };

    fetchPositionData();
  }, [positionId, API]);

  const updateCandidateStage = async (candidateId, newStageId) => {
    try {
      const candidate = Object.values(candidatesByStage)
        .flat()
        .find(c => c._id === candidateId);

      if (!candidate) {
        console.error("Candidate not found:", candidateId);
        setError("Candidat non trouvé");
        return;
      }

      // Trouver l'étape correspondante pour envoyer son ID
      const targetStep = recruitmentSteps.find(s => s._id === newStageId);
      if (!targetStep) {
        console.error("Step not found:", newStageId);
        setError("Étape non trouvée");
        return;
      }

      await API.put(
        `/api/positions/${positionId}/candidate/${candidateId}/stage`,
        { newStage: targetStep._id } // Envoyer l'ID de l'étape
      );

    } catch (err) {
      console.error("Error updating candidate stage:", err);
      setError("Une erreur s'est produite lors de la mise à jour du candidat.");
    }
  };

  // Trouver le conteneur d'un élément par son ID
  const findContainer = (id) => {
    if (id in candidatesByStage) return id;
    
    return Object.keys(candidatesByStage).find(key => 
      candidatesByStage[key].some(item => item._id === id)
    );
  };

  // Gestionnaire de début de drag
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
  };

  // Gestionnaire de fin de drag
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id);
    
    if (!activeContainer || !overContainer) {
      setActiveId(null);
      return;
    }

    const activeIndex = candidatesByStage[activeContainer].findIndex(
      item => item._id === active.id
    );
    
    if (activeContainer === overContainer) {
      // Réorganisation dans le même conteneur
      const overIndex = candidatesByStage[overContainer].findIndex(
        item => item._id === over.id
      );
      
      if (activeIndex !== overIndex) {
        setCandidatesByStage(prev => ({
          ...prev,
          [overContainer]: arrayMove(
            prev[overContainer],
            activeIndex,
            overIndex
          ),
        }));
      }
    } else {
      // Déplacement vers un autre conteneur
      setCandidatesByStage(prev => {
        const candidate = prev[activeContainer][activeIndex];
        
        return {
          ...prev,
          [activeContainer]: [
            ...prev[activeContainer].filter(item => item._id !== active.id),
          ],
          [overContainer]: [
            ...prev[overContainer],
            { ...candidate, stageId: overContainer },
          ],
        };
      });

      // Mise à jour dans la base de données
      updateCandidateStage(active.id, overContainer);
    }

    setActiveId(null);
  };

  // Gestionnaire d'annulation de drag
  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Fonctions de gestion de la sélection
  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => 
      prev.includes(candidateId) 
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectColumn = (_columnId, candidateIds, selectAll) => {
    setSelectedCandidates(prev => {
      if (selectAll) {
        // Ajouter tous les candidats de cette colonne
        const newIds = candidateIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      } else {
        // Retirer tous les candidats de cette colonne
        return prev.filter(id => !candidateIds.includes(id));
      }
    });
  };

  const handleNavigateToIndividual = (individualId) => {
    // Naviguer vers la page de détail du candidat avec effet sidebar
    navigate(`/positions/${positionId}/candidates/${individualId}`);
  };

  const handleClearSelection = () => {
    setSelectedCandidates([]);
  };

  // Gestionnaire pour les filtres de colonne
  const handleColumnFilterChange = (columnId, filters) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: filters
    }));
  };

  // Fonction pour filtrer les candidats d'une colonne
  const getFilteredCandidates = (columnId, candidates) => {
    const filters = columnFilters[columnId];

    if (!filters || (!filters.hasProEmail && !filters.hasPersoEmail && !filters.hasPhone)) {
      return candidates;
    }

    return candidates.filter(candidate => {
      const individual = candidate.individual;
      if (!individual) return false;

      const checks = [];

      if (filters.hasProEmail) {
        const hasProfessionalEmail = getAllEmails(individual.professionalEmail).length > 0;
        checks.push(hasProfessionalEmail);
      }

      if (filters.hasPersoEmail) {
        const hasPersonalEmail = getAllEmails(individual.personalEmail).length > 0;
        checks.push(hasPersonalEmail);
      }

      if (filters.hasPhone) {
        const phoneString = individual.phoneNumber?.trim().toLowerCase();
        const hasValidPhone = phoneString &&
          phoneString !== '-' &&
          phoneString !== 'téléphone indisponible' &&
          phoneString !== 'null' &&
          phoneString !== 'telephone indisponible';
        checks.push(hasValidPhone);
      }

      // Si aucun filtre n'est actif, afficher tous les candidats
      if (checks.length === 0) return true;

      // Si au moins un filtre est actif, le candidat doit satisfaire TOUS les filtres actifs
      return checks.every(check => check === true);
    });
  };

  // Fonction pour mettre à jour l'état local après les bulk actions
  const handleUpdateCandidates = (candidateObjects, newStage, action) => {
    setCandidatesByStage(prev => {
      const newState = { ...prev };
      
      if (action === 'move') {
        // Déplacer les candidats vers la nouvelle étape
        candidateObjects.forEach(candidate => {
          // Trouver et retirer le candidat de son étape actuelle
          Object.keys(newState).forEach(stage => {
            newState[stage] = newState[stage].filter(c => c._id !== candidate._id);
          });
          
          // Ajouter le candidat à la nouvelle étape
          const updatedCandidate = { ...candidate, stage: newStage };
          if (!newState[newStage]) newState[newStage] = [];
          newState[newStage].push(updatedCandidate);
        });
      } else if (action === 'remove') {
        // Retirer les candidats de toutes les étapes
        candidateObjects.forEach(candidate => {
          Object.keys(newState).forEach(stage => {
            newState[stage] = newState[stage].filter(c => c._id !== candidate._id);
          });
        });
      }
      
      return newState;
    });
  };

  const handleRefresh = useCallback(async () => {
    // Recharger les données sans recharger la page
    try {
      setLoading(true);
      const response = await API.get(`/api/positions/${positionId}`);
      const positionData = response.data;

      // Récupérer TOUTES les étapes (pour trouver les stageId des candidats)
      const allSteps = positionData.steps.sort((a, b) => a.order - b.order);

      // Filtrer les étapes actives pour l'affichage
      const activeSteps = allSteps.filter(step => step.active);

      setRecruitmentSteps(activeSteps);

      // Groupement des candidats par étape (en cherchant dans TOUTES les étapes)
      const grouped = positionData.candidates.reduce((acc, candidate) => {
        let stageId;

        // Priorité à stageId si disponible
        if (candidate.stageId) {
          // ✅ Chercher dans TOUTES les étapes, pas seulement les actives
          const step = allSteps.find(s => s._id === candidate.stageId);
          stageId = step ? step._id : null;
        }

        // Fallback sur stage (nom) pour compatibilité
        if (!stageId) {
          const step = allSteps.find(s => s.name === candidate.stage);
          stageId = step ? step._id : null;
        }

        // ✅ IMPORTANT : N'ajouter le candidat QUE si son étape est ACTIVE
        if (stageId) {
          const stepIsActive = activeSteps.find(s => s._id === stageId);
          if (stepIsActive) {
            if (!acc[stageId]) acc[stageId] = [];
            acc[stageId].push(candidate);
          }
          // Si l'étape n'est pas active, le candidat reste dans son étape
          // mais n'est pas affiché dans le Kanban
        }

        return acc;
      }, {});

      // S'assurer que toutes les étapes actives existent, même vides
      const completeGrouped = activeSteps.reduce((acc, step) => {
        acc[step._id] = grouped[step._id] || [];
        return acc;
      }, {});

      setCandidatesByStage(completeGrouped);
      setPositionData(positionData);
      setError(null);
    } catch (err) {
      console.error("Error refreshing candidates:", err);
      setError("Erreur lors du rechargement des candidats.");
    } finally {
      setLoading(false);
    }
  }, [API, positionId]);

  // Écouter les événements de mise à jour de candidat
  useEffect(() => {
    const handleCandidateStageUpdated = () => {
      handleRefresh();
    };

    window.addEventListener('candidateStageUpdated', handleCandidateStageUpdated);

    return () => {
      window.removeEventListener('candidateStageUpdated', handleCandidateStageUpdated);
    };
  }, [handleRefresh]);

  // Calculs des statistiques
  const calculateStats = () => {
    if (!positionData) return { totalCandidates: 0, conversionRate: 0, activeSteps: 0 };

    const totalCandidates = positionData.candidates.length;
    const activeSteps = recruitmentSteps.filter(step => step.active).length;

    // Calculer le taux de conversion (candidats dans les étapes finales positives)
    const okStep = recruitmentSteps.find(step => step.name.toLowerCase() === 'ok');
    const okCandidates = okStep ? (candidatesByStage[okStep._id] || []).length : 0;
    const conversionRate = totalCandidates > 0 ? ((okCandidates / totalCandidates) * 100).toFixed(1) : 0;

    return {
      totalCandidates,
      conversionRate: parseFloat(conversionRate),
      activeSteps,
    };
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/api/positions/${positionId}`);
      navigate('/process-rh');
    } catch (error) {
      console.error('Error deleting position:', error);
      setError('Erreur lors de la suppression du poste.');
    }
  };

  const stats = calculateStats();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div>Chargement des candidats...</div>
      </div>
    );
  }

  // Affichage des erreurs
  if (error) {
    return (
      <div className="text-red-500 p-4 text-center">
        {error}
      </div>
    );
  }

  // Rendu principal
  return (
    <div className="   bg-gray-100 min-h-screen">
      {/* Header avec informations du poste */}
      <PositionHeader
        positionData={positionData}
        stats={stats}
        onEditClick={() => setShowEditModal(true)}
        onDeleteClick={() => setShowDeleteDialog(true)}
        showDeleteButton={true}
        onAddCandidateClick={() => setShowAddCandidateModal(true)}
        showAddCandidateButton={true}
      />

      <div className="flex justify-left py-2">
        <div className="inline-flex rounded-lg border border-gray-300 bg-white ">
          <button
            onClick={() => setViewMode('kanban')}
            className={`px-2 py-1 rounded-l-md text-sm font-medium transition-colors 
              ${viewMode === 'kanban' ? ' text-gray-700 hover:text-gray-900' : 'bg-gray-100 text-gray-700 hover:text-gray-900'}`}
          >
            <Columns3 size={20}/>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-2 py-1 rounded-r-md text-sm font-medium transition-colors 
              ${viewMode === 'list' ? ' text-gray-700 hover:text-gray-900' : 'bg-gray-100 text-gray-700 hover:text-gray-900'}`}
          >
            <Rows3 size={20}/>
          </button>
        </div>

      </div>

      {selectedCandidates.length > 0 && (
        <RecruitmentBulkActions
          selectedCandidates={selectedCandidates}
          positionId={positionId}
          onClearSelection={handleClearSelection}
          onRefresh={handleRefresh}
          onUpdateCandidates={handleUpdateCandidates}
        />
      )}

      {/* Tableau Kanban */}
      <div className="flex-1">
        <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
          {viewMode === 'kanban' ? (
            <div style={wrapperStyle}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                {recruitmentSteps.map((step) => {
                  const allCandidates = candidatesByStage[step._id] || [];
                  const filteredCandidates = getFilteredCandidates(step._id, allCandidates);

                  return (
                    <RecruitmentContainer
                      key={step._id}
                      id={step._id}
                      title={step.name}
                      items={filteredCandidates}
                      allItems={allCandidates}
                      selectedCandidates={selectedCandidates}
                      onSelectCandidate={handleSelectCandidate}
                      onSelectColumn={handleSelectColumn}
                      onNavigateToIndividual={handleNavigateToIndividual}
                      filters={columnFilters[step._id]}
                      onFilterChange={(filters) => handleColumnFilterChange(step._id, filters)}
                    />
                  );
                })}

                <DragOverlay dropAnimation={dropAnimation}>
                  {activeId ? (
                    <RecruitmentItem
                      candidate={
                        Object.values(candidatesByStage)
                          .flat()
                          .find((candidate) => candidate._id === activeId)
                      }
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          ) : (
              <RecruitmentListView
                candidatesByStage={candidatesByStage}
                recruitmentSteps={recruitmentSteps}
                selectedCandidates={selectedCandidates}
                onSelectCandidate={handleSelectCandidate}
                onNavigateToIndividual={handleNavigateToIndividual}
                columnFilters={columnFilters}
                getFilteredCandidates={getFilteredCandidates}
                onMoveToNextStage={updateCandidateStage}
                onRefresh={handleRefresh}
              />
          )}

        </div>
      </div>

      {/* Modal d'édition */}
      <EditPosition
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        position={positionData}
        onPositionUpdated={handleRefresh}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Supprimer le poste"
        message={`Êtes-vous sûr de vouloir supprimer le poste "${positionData?.title}" ? Cette action est irréversible et supprimera également tous les candidats associés.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />

      {/* Modal d'ajout de candidat */}
      {showAddCandidateModal && (
        <AddIndividual
          onClose={() => setShowAddCandidateModal(false)}
          refreshIndividuals={handleRefresh}
          preSelectedPosition={positionId}
          preSelectedStage={recruitmentSteps.find(step => step.active)?._id || ''}
        />
      )}
    </div>
  );
}